import { useForm } from 'react-hook-form';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Form from 'storybookConfig/stories/organisms/Form';
import { useEffect, useState } from 'react';
import Flex from '@react-css/flex';
import Body from 'storybookConfig/stories/molecules/Body';
import useAlertQueue from 'hooks/useAlertQueue';
import { useAppSelector, useAppDispatch } from 'store/index';
import { selectCompanyId } from 'store/selectors/me/company';
import { updateMarketplaceOnboarding } from 'store/slices/me';
import { updateCompany } from 'utils/api/companies';
import { useMutation } from '@tanstack/react-query';
import type { UploadedFile } from 'storybookConfig/stories/cells/FileUpload';
import FileUpload from 'storybookConfig/stories/cells/FileUpload';
import Icon from 'storybookConfig/stories/molecules/Icon';
import { uploadPublicImage } from 'utils/api/documents';
import FormField from 'storybookConfig/stories/cells/FormField';
import TertiaryButton from 'storybookConfig/stories/molecules/Button/TertiaryButton';
import type { FormProps } from './types';
import useSegment from 'hooks/useSegment';

interface FormFields {
  brandProfilePhoto: string;
  brandHeaderPhoto: string;
  brandCardPhoto: string;
}

const BrandAssetsForm = ({ onNext, onBack, marketplaceData, setMarketplaceData }: FormProps) => {
  const companyId = useAppSelector(selectCompanyId);
  const dispatch = useAppDispatch();
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();
  const analytics = useSegment();
  const [brandProfilePhoto, setBrandProfilePhoto] = useState<string | null>(
    marketplaceData?.brandProfilePhoto || null
  );
  const [brandHeaderPhoto, setBrandHeaderPhoto] = useState<string | null>(
    marketplaceData?.brandHeaderPhoto || null
  );
  const [brandCardPhoto, setBrandCardPhoto] = useState<string | null>(
    marketplaceData?.brandCardPhoto || null
  );

  const { handleSubmit, formState, register, setError, clearErrors } = useForm<FormFields>({
    mode: 'onChange',
    values: {
      brandProfilePhoto: marketplaceData?.brandProfilePhoto || '',
      brandHeaderPhoto: marketplaceData?.brandHeaderPhoto || '',
      brandCardPhoto: marketplaceData?.brandCardPhoto || '',
    },
  });

  // Register custom file upload fields once using useEffect
  useEffect(() => {
    register('brandProfilePhoto');
    register('brandHeaderPhoto');
    register('brandCardPhoto');
  }, [register]);

  const handleImageSelect = (field: keyof FormFields) => async (uploadedFile: UploadedFile) => {
    if (!uploadedFile.file) return;

    try {
      const file = new File([uploadedFile.file], uploadedFile.fileName, {
        type: uploadedFile.fileType,
      });

      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        addErrorAlert('File too large', 'Please select an image smaller than 5MB');
        return;
      }

      await uploadPublicImage(file, {
        onPreview: (url) => {
          if (field === 'brandProfilePhoto') {
            setBrandProfilePhoto(url);
            setMarketplaceData({ brandProfilePhoto: url });
          } else if (field === 'brandHeaderPhoto') {
            setBrandHeaderPhoto(url);
            setMarketplaceData({ brandHeaderPhoto: url });
          } else if (field === 'brandCardPhoto') {
            setBrandCardPhoto(url);
            setMarketplaceData({ brandCardPhoto: url });
          }
        },
        onComplete: (url) => {
          if (field === 'brandProfilePhoto') {
            setBrandProfilePhoto(url);
            setMarketplaceData({ brandProfilePhoto: url });
          } else if (field === 'brandHeaderPhoto') {
            setBrandHeaderPhoto(url);
            setMarketplaceData({ brandHeaderPhoto: url });
          } else if (field === 'brandCardPhoto') {
            setBrandCardPhoto(url);
            setMarketplaceData({ brandCardPhoto: url });
          }
        },
        onError: (error) => {
          console.error('Error uploading image', error);
          addErrorAlert('Something went wrong', 'Unable to upload image');
        },
      });
    } catch (error) {
      console.error('Error uploading image', error);
      addErrorAlert('Something went wrong', 'Unable to upload image');
    }
  };

  // Validation helper
  const validateRequiredFiles = () => {
    const errors: { [key: string]: string } = {};

    if (!brandProfilePhoto) {
      errors.brandProfilePhoto = 'Brand Profile Photo is required';
    }
    if (!brandHeaderPhoto) {
      errors.brandHeaderPhoto = 'Company Header Image is required';
    }
    if (!brandCardPhoto) {
      errors.brandCardPhoto = 'Brand Card Image is required';
    }

    return errors;
  };

  const updatingCompany = useMutation({
    mutationFn: async () => {
      const updatedData = {
        brandProfilePhoto: brandProfilePhoto || '',
        brandHeaderPhoto: brandHeaderPhoto || '',
        brandCardPhoto: brandCardPhoto || '',
      };
      setMarketplaceData(updatedData);
      return updateCompany(companyId ?? '', {
        marketplaceSettings: updatedData,
      });
    },
    onSuccess: (data) => {
      analytics?.track('Supplier Onboarding Brand Assets Completed', {
        profile_photo_uploaded: !!data.marketplaceSettings.brandProfilePhoto,
        header_image_uploaded: !!data.marketplaceSettings.brandHeaderPhoto,
        brand_card_uploaded: !!data.marketplaceSettings.brandCardPhoto,
      });
      dispatch(updateMarketplaceOnboarding(data.marketplaceSettings));
      addSuccessAlert('Added Brand Assets', 'Your brand assets have been saved.');
      if (onNext) {
        onNext();
      }
    },
    onError: (error: { message: string }) => {
      analytics?.track('Supplier Onboarding Step Error', {
        step_name: 'assets',
        error_message: error.message,
      });
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update marketplace settings', error);
    },
  });

  const renderUploadContent = (label: string) => (
    <Flex gap="16px" alignItemsCenter justifyCenter>
      <Icon name="upload_file" size="48px" color="iconDefault" />
      <Flex.Item grow={1}>
        <Body color="bodyTextSecondary" variant="Body/Regular">
          Drag and drop or <strong>click here to {label}</strong>
        </Body>
      </Flex.Item>
    </Flex>
  );

  const handleFormSubmit = () => {
    // Clear any existing errors
    clearErrors(['brandProfilePhoto', 'brandHeaderPhoto', 'brandCardPhoto']);

    const errors = validateRequiredFiles();
    if (Object.keys(errors).length > 0) {
      // Set errors on the form
      Object.entries(errors).forEach(([field, message]) => {
        setError(field as keyof FormFields, { message });
      });
      return;
    }

    updatingCompany.mutate();
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <Flex>
        <TertiaryButton
          type="button"
          $removePadding="left"
          onClick={onBack}
          $iconName="navigate_before"
        >
          Back
        </TertiaryButton>
      </Flex>
      <Heading variant="Headings/H3">Brand Assets</Heading>
      <Form.FieldGroup>
        <Flex column gap="4px">
          <FormField
            isRequired
            labelText="Brand Profile Photo"
            id="brandProfilePhoto"
            errorText={formState.errors.brandProfilePhoto?.message}
          >
            <FileUpload
              onUpload={handleImageSelect('brandProfilePhoto')}
              borderRadius="8px"
              previewUrl={brandProfilePhoto || undefined}
              id="brandProfilePhotoUpload"
            >
              {renderUploadContent('upload profile photo')}
            </FileUpload>
          </FormField>
          <Body color="bodyTextSecondary" style={{ marginTop: '8px' }}>
            To keep it from stretching, please upload photos with dimensions of 300 x 300 or an
            aspect ratio of 1:1. We support JPG, JPEG, and PNG images
          </Body>
        </Flex>

        <Flex column gap="4px">
          <FormField
            isRequired
            labelText="Company Header Image"
            id="brandHeaderPhoto"
            errorText={formState.errors.brandHeaderPhoto?.message}
          >
            <FileUpload
              onUpload={handleImageSelect('brandHeaderPhoto')}
              borderRadius="8px"
              previewUrl={brandHeaderPhoto || undefined}
              id="brandHeaderPhotoUpload"
            >
              {renderUploadContent('upload header image')}
            </FileUpload>
          </FormField>
          <Body color="bodyTextSecondary" style={{ marginTop: '8px' }}>
            To keep it from stretching, please upload photos with dimensions of 844 x 240 or an
            aspect ratio of 3.52:1. We support JPG, JPEG, and PNG images
          </Body>
        </Flex>
        <Flex column gap="4px">
          <FormField
            isRequired
            labelText="Brand Card Image"
            id="brandCardPhoto"
            errorText={formState.errors.brandCardPhoto?.message}
          >
            <FileUpload
              onUpload={handleImageSelect('brandCardPhoto')}
              borderRadius="8px"
              previewUrl={brandCardPhoto || undefined}
              id="brandCardPhotoUpload"
            >
              {renderUploadContent('upload card image')}
            </FileUpload>
          </FormField>
          <Body color="bodyTextSecondary" style={{ marginTop: '8px' }}>
            To keep it from stretching, please upload photos with dimensions of 236 x 254 or an
            aspect ratio of 0.93:1. We support JPG, JPEG, and PNG images
          </Body>
        </Flex>
      </Form.FieldGroup>

      <Form.Footer column gap="16px">
        <PrimaryButton type="submit" disabled={updatingCompany.status === 'loading'}>
          {updatingCompany.status === 'loading' ? 'Saving...' : 'Next'}
        </PrimaryButton>
      </Form.Footer>
    </Form>
  );
};

export default BrandAssetsForm;
